/**
 * Event Source show — `[PROPOSED]` Inspector-grammar teaser at
 * `/event-sources/:id`. Renders the full grammar shell (Header / Overview /
 * Body / Relations / Audit) with the named primary actions present as disabled
 * affordances (**enable / disable · trigger sync**, `frontend-entity-coverage
 * §2.3`) until `/api/event-sources` ships. Never hard-errors a show page.
 */
import { useParams } from "react-router-dom";
import { Ban, Power, RefreshCw } from "@/components/ui/icon";

import { Button } from "@/components/ui/button";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RelationsBlock,
  AuditBlock,
  TechnicalId,
} from "@/components/inspector";
import {
  ProposedShowTeaser,
  BackToList,
} from "../_shared/ProposedModule";

export function EventSourceShow() {
  const { id } = useParams<{ id: string }>();

  return (
    <Inspector>
      <InspectorHeader
        title="Event Source"
        subtitle="A connector that stamps inbound events — its health and last sync."
        status="proposed"
        primaryAction={
          <Button size="sm" variant="outline" disabled>
            <RefreshCw className="mr-2 h-4 w-4" /> Trigger sync
          </Button>
        }
        actions={<BackToList to="/event-sources" label="Event sources" />}
        menuActions={[
          { label: "Enable", icon: Power, disabled: true, onClick: () => {} },
          { label: "Disable", icon: Ban, disabled: true, onClick: () => {} },
        ]}
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Source type" />
          <Field label="Status" />
          <Field label="Health" />
          <Field label="Last sync" />
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Body">
        <ProposedShowTeaser endpoint="/api/event-sources" />
      </InspectorSection>

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "Stamped",
              emptyMessage: "Events stamped by this source appear once it ships.",
              items: [],
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={String(id)} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="event-sources" entityId={id} />
      </InspectorSection>
    </Inspector>
  );
}

export default EventSourceShow;
